import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CircleDot, X } from 'lucide-react';
import { Game, PracticeShot, ShotType, Turn } from '../types/curling';
import { getPracticeSession } from '../utils/storage';
import {
  estimateQuadrantTime,
  estimateBinnedCurl,
  endTypeFromEndNumber,
  sideFromBroomPosition,
  turnToDirection,
  QuadrantTimeEstimate,
  QuadrantCurlEstimate,
} from '../utils/teeLineEstimate';

export interface ShotVisualizerAppliedValues {
  targetZone: number;
  broomPosition: number;
  turn: Turn;
  expectedCurl: number;
  expectedTime: number;
}

interface ShotVisualizerProps {
  game: Game;
  endNumber: number;
  /** Extra classes for the trigger button, e.g. flex-1 to share a row evenly with a sibling. */
  className?: string;
  /** Called when Set is pressed, with the planned shot's expected values — the
   *  caller is responsible for writing them into its own form state. */
  onApply?: (values: ShotVisualizerAppliedValues) => void;
}

// The six fixed-weight hit types — each its own setup value (or, for Hack,
// the quadrant baseline plus a fixed offset), never something the broom's
// position in the house could sensibly change, since none of them land at a
// specific spot in the house the way a draw does. The key strings double as
// this game's ShotType for the same zone (see CURL_FROM_ACTUAL_WEIGHT_ZONES
// below), since they happen to match exactly.
const HIT_ZONES: Array<{ zone: number; abbr: string; label: string }> = [
  { zone: 11, abbr: 'Hk', label: 'Hack' },
  { zone: 12, abbr: 'Brd', label: 'Board' },
  { zone: 13, abbr: 'Ctl', label: 'Control' },
  { zone: 14, abbr: 'Nrm', label: 'Normal' },
  { zone: 15, abbr: 'Fm', label: 'Firm' },
  { zone: 16, abbr: 'Pl', label: 'Peel' },
];

const HIT_ZONE_SETUP_KEY: Partial<Record<number, keyof NonNullable<Game['expectedTimes']>>> = {
  11: 'hack', 12: 'board', 13: 'control', 14: 'normal', 15: 'firm', 16: 'peel',
};

// Curl doesn't scale sensibly from draw-weight data at these speeds — control
// through peel weight get their curl only from other shots actually thrown
// at that exact weight (see estimateBinnedCurl's shotTypeFilter), never from
// scaling.
const CURL_FROM_ACTUAL_WEIGHT_ZONES = new Set([13, 14, 15, 16]);

/**
 * Trigger button + modal for planning a single shot: pick Draw or a fixed
 * hit type, drag the broom to where you'd place it, and see the estimated
 * weight and curl for that exact combination — the curl drawn as a rock
 * silhouette offset from the broom by the estimated amount, in the direction
 * that turn actually curls. For Draw, both weight and curl come from the
 * broom's dragged position in the house (there's no separate zone button —
 * the house image itself is the only zone picker); a hit type's weight is
 * fixed by that type instead, since none of them land at a specific spot in
 * the house the way a draw does. Weight comes from the same per-quadrant
 * (broom side + turn + end) cascade the Sheet Visualizer uses; curl is finer
 * — binned by the broom's actual dragged position in 1ft slices rather than
 * just which half of the sheet it's on (see estimateBinnedCurl in
 * utils/teeLineEstimate) — without the Sheet Visualizer's four preset arrows
 * or the away/home toggle, since the end is whichever one is currently being
 * recorded.
 */
export function ShotVisualizer({ game, endNumber, className, onApply }: ShotVisualizerProps) {
  const [practiceShots, setPracticeShots] = useState<PracticeShot[]>([]);
  const [show, setShow] = useState(false);
  // Clockwise curls the rock to the right of the broom (see turnToDirection) —
  // the only turn for which "broom left of rock" is even possible as a
  // default, matching the Record Shot screen's own broom/rock diagram default.
  const [turn, setTurn] = useState<Turn>('clockwise');
  const [shotMode, setShotMode] = useState<'draw' | number>('draw');

  const W = 340;
  const H = 500;
  const centerX = W / 2;
  const hogLineY = 40;
  const teeLineY = 300;
  const boardsY = H - 30;
  // One consistent scale for the whole diagram, so a curl distance in feet
  // and a distance read off the rings agree — house rings are named by
  // diameter (a "12-foot" ring is 12ft across, i.e. a 6ft radius), and the
  // back line sits exactly where the 12-ft ring's radius puts it, not at an
  // independently-chosen pixel value.
  const PX_PER_FOOT = 20;
  const radii = {
    twelve: 6 * PX_PER_FOOT,
    eight: 4 * PX_PER_FOOT,
    four: 2 * PX_PER_FOOT,
    button: 0.5 * PX_PER_FOOT,
  };
  const backLineY = teeLineY + radii.twelve;
  const houseFrontY = teeLineY - radii.twelve;

  // Draw weight and curl-scaling both key off how far up/down the house the
  // broom sits — these are the same reference points (and the same offsets)
  // ShotTracker's own expected-time table uses for zones 1-11, just read off
  // the broom's actual y instead of a button. Interpolated linearly between
  // neighbors, clamped to the nearest anchor past either end.
  const drawAnchors: Array<{ y: number; zone: number; offset: number }> = [
    { y: hogLineY + (houseFrontY - hogLineY) * 0.25, zone: 1, offset: 4.0 },
    { y: hogLineY + (houseFrontY - hogLineY) * 0.5, zone: 2, offset: 3.0 },
    { y: hogLineY + (houseFrontY - hogLineY) * 0.75, zone: 3, offset: 1.5 },
    { y: houseFrontY, zone: 4, offset: 0.75 },
    { y: teeLineY - radii.eight, zone: 5, offset: 0.5 },
    { y: teeLineY - radii.four, zone: 6, offset: 0.25 },
    { y: teeLineY, zone: 7, offset: 0 },
    { y: teeLineY + radii.four, zone: 8, offset: -0.25 },
    { y: teeLineY + radii.eight, zone: 9, offset: -0.5 },
    { y: backLineY, zone: 10, offset: -0.75 },
    { y: backLineY + 7, zone: 11, offset: -2 },
  ];
  const interpolateDrawAnchor = (y: number, key: 'zone' | 'offset'): number => {
    const first = drawAnchors[0];
    const last = drawAnchors[drawAnchors.length - 1];
    if (y <= first.y) return first[key];
    if (y >= last.y) return last[key];
    for (let i = 0; i < drawAnchors.length - 1; i++) {
      const a = drawAnchors[i];
      const b = drawAnchors[i + 1];
      if (y >= a.y && y <= b.y) {
        const t = (y - a.y) / (b.y - a.y);
        return a[key] + t * (b[key] - a[key]);
      }
    }
    return last[key];
  };

  // The broom is a free-floating marker, independent of the turn/zone
  // selectors — dragging it never changes the selection, it just moves where
  // the curl offset is measured from. y is stored in logical (unflipped)
  // space, like every other y in this diagram, so Y() renders it consistently.
  const [broomPos, setBroomPos] = useState({ x: centerX, y: teeLineY });
  const isDraggingRef = useRef(false);
  // Once the user has actually dragged the broom, stop auto-centering the
  // rock on it — that correction is only for the untouched initial default.
  const hasUserMovedBroomRef = useRef(false);

  // Signed feet from the broom, same convention as expectedCurl everywhere
  // else (positive = right of broom = clockwise) — set by dragging the rock,
  // overriding the historical estimate when the team doesn't expect the
  // same curl this game/end has been showing. null until touched, so the
  // estimate keeps driving the rock for anyone who never drags it.
  const [manualCurl, setManualCurl] = useState<number | null>(null);
  const isDraggingRockRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getPracticeSession(game.id)
      .then(session => { if (!cancelled) setPracticeShots(session.shots ?? []); })
      .catch(error => console.error('Failed to load practice shots for the Shot Visualizer:', error));
    return () => { cancelled = true; };
  }, [game.id]);

  const endType = endTypeFromEndNumber(endNumber);
  const side = sideFromBroomPosition(broomPos.x - centerX);
  const direction = turnToDirection(turn);
  const quadrant = { endType, side, direction };
  const isLive = !game.isComplete;
  // Broom position in feet from center, for the curl estimate's 1ft bins —
  // weight stays on the coarser side-based quadrant above, curl doesn't.
  const broomFeet = (broomPos.x - centerX) / PX_PER_FOOT;

  const quadrantTimeEstimate = estimateQuadrantTime(game, practiceShots, quadrant, isLive);
  const curlEstimate = estimateBinnedCurl(game, practiceShots, endType, broomFeet, direction, isLive);

  // Draw: the quadrant's tee-line baseline plus the offset interpolated from
  // wherever the broom actually sits in the house — no zone button involved.
  // Hit types: their own setup value (Hack included, now that it's grouped
  // with the other fixed-weight hit types rather than treated as a draw).
  // Falls back to the baseline itself, flagged as an estimate, if that
  // weight was never configured for this game.
  const zoneTimeEstimate = ((): QuadrantTimeEstimate => {
    if (shotMode === 'draw') {
      const offset = interpolateDrawAnchor(broomPos.y, 'offset');
      return { ...quadrantTimeEstimate, time: quadrantTimeEstimate.time + offset };
    }
    const key = HIT_ZONE_SETUP_KEY[shotMode];
    const setupValue = key ? game.expectedTimes?.[key] : undefined;
    if (setupValue !== undefined) {
      return { time: setupValue, source: 'baseline', isEstimate: false, shotsUsed: 0 };
    }
    return { ...quadrantTimeEstimate, isEstimate: true };
  })();

  // Curl scales with how long the rock is on the ice: a softer, longer-hanging
  // guard weight curls a bit more than a draw to the button, which curls a bit
  // more than a harder takeout weight. This is already only an estimate (the
  // baseline itself comes from other zones' shots, not this one), so rather
  // than scale by the timing offset — which compounds fast for positions far
  // from the tee line and overstates the curl — each zone-equivalent step
  // away from 7 (the button) adjusts the baseline by a flat 2.5%, using the
  // same broom-position interpolation as weight for Draw, or the hit type's
  // own fixed zone number otherwise. Control/normal/firm/peel are thrown too
  // hard for that relationship to hold at all, so those skip scaling
  // entirely and come only from other shots actually thrown at that weight.
  const zoneCurlEstimate = ((): QuadrantCurlEstimate => {
    if (shotMode !== 'draw' && CURL_FROM_ACTUAL_WEIGHT_ZONES.has(shotMode)) {
      const shotType = HIT_ZONE_SETUP_KEY[shotMode] as ShotType;
      return estimateBinnedCurl(game, practiceShots, endType, broomFeet, direction, isLive, shotType);
    }
    const zoneEquivalent = shotMode === 'draw' ? interpolateDrawAnchor(broomPos.y, 'zone') : shotMode;
    const multiplier = 1 + 0.025 * (7 - zoneEquivalent);
    return { ...curlEstimate, curl: curlEstimate.curl * multiplier, isEstimate: true };
  })();

  // Defaulting the broom itself to dead center would leave the rock (broom +
  // estimated curl) sitting off the button. Nudge the broom's default x to
  // the left by the estimated curl instead, so the rock starts on the button
  // with the broom to its left — the same default relationship the main
  // Record Shot screen's own broom/rock diagram starts in. Re-applies every
  // time practiceShots changes (in practice just once, when the async fetch
  // resolves) rather than only once ever — the first fire happens on mount,
  // before that fetch has resolved, so gating on a "done it once" ref would
  // permanently lock in a correction computed from zero practice data. A
  // real user drag permanently opts out via hasUserMovedBroomRef.
  useEffect(() => {
    if (hasUserMovedBroomRef.current) return;
    const px = Math.abs(zoneCurlEstimate.curl) * PX_PER_FOOT;
    setBroomPos(pos => ({ ...pos, x: Math.min(Math.max(centerX - px, 25), W - 25) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceShots]);

  const flip = endType === 'away';
  const Y = (y: number) => (flip ? H - y : y);

  // Drag the broom by its handle — pointer capture keeps the drag going even
  // if the cursor slips off the small hit target, and releases cleanly on
  // pointer-up or if the pointer is cancelled (e.g. it leaves the window).
  const handleBroomPointerDown = (e: React.PointerEvent<SVGGElement>) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    hasUserMovedBroomRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleBroomPointerMove = (e: React.PointerEvent<SVGGElement>) => {
    if (!isDraggingRef.current) return;
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const x = Math.min(Math.max(svgP.x, 25), W - 25);
    const renderY = Math.min(Math.max(svgP.y, 25), H - 25);
    setBroomPos({ x, y: flip ? H - renderY : renderY });
  };

  const handleBroomPointerUp = (e: React.PointerEvent<SVGGElement>) => {
    isDraggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  if (!isLive) return null; // planning the next shot only makes sense while the game is in progress

  const broomRenderX = broomPos.x;
  const broomRenderY = Y(broomPos.y);
  // Curl is drawn as a horizontal offset from the broom, in the direction
  // that turn actually curls — clockwise always drifts right, counter-
  // clockwise always left, regardless of which side of center it started.
  const curlSign = direction === 'left-right' ? 1 : -1;
  // True to the same PX_PER_FOOT scale as the rings, so reading a distance off
  // the diagram — e.g. broom on the 8-ft ring's edge (a 4ft radius) with 4ft of
  // curl lands the rock exactly on the center line — always checks out.
  const estimateCurlPx = Math.abs(zoneCurlEstimate.curl) * PX_PER_FOOT;
  // manualCurl (when set) is a full signed offset from the broom, not just a
  // magnitude — on keen ice a skip can call enough negative ice that the rock
  // is expected to finish on the *other* side of the broom from the turn it
  // was thrown with, so dragging isn't confined to the turn's own side the
  // way the estimate always is.
  const rockOffsetPx = manualCurl !== null ? manualCurl * PX_PER_FOOT : curlSign * estimateCurlPx;
  const rockX = Math.min(Math.max(broomRenderX + rockOffsetPx, 25), W - 25);
  const effectiveCurl = manualCurl !== null ? manualCurl : zoneCurlEstimate.curl;
  // Negative ice: the rock has been dragged past the broom, onto the side
  // opposite the turn it's thrown with — surfaced in the readout below as a
  // signed, red-highlighted value rather than silently folded into the
  // normal (always turn-side) magnitude reading.
  const isNegativeIce = manualCurl !== null && manualCurl !== 0 && Math.sign(manualCurl) !== curlSign;

  // Drag the rock horizontally to override the estimated curl — clamped only
  // to the diagram's own edges, same as the broom, since the rock is now
  // free to cross the broom's line entirely (see isNegativeIce above).
  const handleRockPointerDown = (e: React.PointerEvent<SVGGElement>) => {
    e.stopPropagation();
    isDraggingRockRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleRockPointerMove = (e: React.PointerEvent<SVGGElement>) => {
    if (!isDraggingRockRef.current) return;
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const x = Math.min(Math.max(svgP.x, 25), W - 25);
    setManualCurl((x - broomRenderX) / PX_PER_FOOT);
  };

  const handleRockPointerUp = (e: React.PointerEvent<SVGGElement>) => {
    isDraggingRockRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Draw doesn't have a single zone button behind it — round the broom's
  // continuous position to the nearest whole zone (1-11) so Set still has a
  // real zone number to hand back to the recording form.
  const resolvedZone = shotMode === 'draw'
    ? Math.round(interpolateDrawAnchor(broomPos.y, 'zone'))
    : shotMode;

  const handleApply = () => {
    onApply?.({
      targetZone: resolvedZone,
      broomPosition: broomFeet,
      turn,
      expectedCurl: effectiveCurl,
      expectedTime: zoneTimeEstimate.time,
    });
    setShow(false);
  };

  return (
    <>
      <button
        onClick={() => setShow(true)}
        className={`flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium ${className ?? ''}`}
      >
        <CircleDot className="w-4 h-4" />
        Shot Visualizer
      </button>

      {show && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShow(false)}
        >
          <div
            className="bg-slate-900 border border-white/15 rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold text-white">Shot Visualizer</h2>
              <button onClick={() => setShow(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-1.5 mb-1">
              <button
                onClick={() => setShotMode('draw')}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  shotMode === 'draw' ? 'bg-blue-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/15'
                }`}
              >
                Draw
              </button>
              {HIT_ZONES.map(z => (
                <button
                  key={z.zone}
                  onClick={() => setShotMode(z.zone)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    shotMode === z.zone ? 'bg-amber-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/15'
                  }`}
                >
                  {z.abbr}
                </button>
              ))}
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
              {/* Sheet background */}
              <rect x={20} y={20} width={W - 40} height={H - 40} rx={8} fill="#0f2942" stroke="rgba(255,255,255,0.15)" />

              {/* House rings, centered on the button at the tee line */}
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.twelve} fill="#2563eb" fillOpacity={0.25} stroke="#3b82f6" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.eight} fill="#0f2942" stroke="#e2e8f0" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.four} fill="#dc2626" fillOpacity={0.35} stroke="#ef4444" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.button} fill="#ef4444" style={{ pointerEvents: 'none' }} />

              <line x1={20} y1={Y(hogLineY)} x2={W - 20} y2={Y(hogLineY)} stroke="#f87171" strokeWidth={2.5} style={{ pointerEvents: 'none' }} />
              <text x={W - 24} y={Y(hogLineY) + 14} textAnchor="end" fontSize={9} fill="#f87171" style={{ pointerEvents: 'none' }}>Hog Line</text>

              <line x1={20} y1={Y(teeLineY)} x2={W - 20} y2={Y(teeLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <text x={24} y={Y(teeLineY) + 14} fontSize={9} fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }}>Tee</text>

              <line x1={20} y1={Y(backLineY)} x2={W - 20} y2={Y(backLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <text x={24} y={Y(backLineY) + 14} fontSize={9} fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }}>Back Line</text>

              <line x1={20} y1={Y(boardsY)} x2={W - 20} y2={Y(boardsY)} stroke="#94a3b8" strokeWidth={4} style={{ pointerEvents: 'none' }} />
              {/* Offset toward the diagram's interior (the tee line), not just "below" —
                  the boards line sits near whichever edge is on top for this end, so a
                  fixed downward offset would push the label off-canvas in the other
                  orientation. */}
              <text
                x={centerX}
                y={Y(boardsY) + (Y(teeLineY) > Y(boardsY) ? 16 : -10)}
                textAnchor="middle"
                fontSize={9}
                fill="#94a3b8"
                style={{ pointerEvents: 'none' }}
              >
                Back Boards
              </text>

              <line x1={centerX} y1={Y(hogLineY)} x2={centerX} y2={Y(backLineY)} stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} strokeDasharray="4 3" style={{ pointerEvents: 'none' }} />

              {/* Rock silhouette: offset from the broom by the estimated curl (or the
                  user's own drag, once they've overridden it), toward the side that
                  turn actually breaks. Draggable horizontally only — it always stays
                  level with the broom, same as before, just no longer glued to the
                  estimate. A larger invisible circle makes it easier to grab than the
                  11px silhouette alone, same trick the broom head uses. */}
              <g
                onPointerDown={handleRockPointerDown}
                onPointerMove={handleRockPointerMove}
                onPointerUp={handleRockPointerUp}
                onPointerCancel={handleRockPointerUp}
                style={{ cursor: 'grab', touchAction: 'none' }}
              >
                <circle cx={rockX} cy={broomRenderY} r={18} fill="transparent" />
                <circle cx={rockX} cy={broomRenderY} r={11} fill="#ffffff" stroke="#ec4899" strokeWidth={3} />
                <circle cx={rockX} cy={broomRenderY} r={3} fill="#ec4899" />
              </g>

              {/* Set — applies this exact planned shot (zone, broom position, turn,
                  expected curl and weight) back to the recording form and closes. */}
              {onApply && (
                <g onClick={handleApply} style={{ cursor: 'pointer' }}>
                  <rect
                    x={Math.min(Math.max(rockX - 20, 22), W - 22 - 40)}
                    y={Math.max(broomRenderY - 34, 24) - 9}
                    width={40}
                    height={18}
                    rx={9}
                    fill="#22c55e"
                    stroke="#15803d"
                    strokeWidth={1.5}
                  />
                  <text
                    x={Math.min(Math.max(rockX, 22 + 20), W - 22 - 20)}
                    y={Math.max(broomRenderY - 34, 24) + 4}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight="bold"
                    fill="#ffffff"
                    style={{ pointerEvents: 'none' }}
                  >
                    Set
                  </text>
                </g>
              )}

              {/* Estimated weight/curl readout — floats just under the rock and follows
                  it around instead of sitting in static boxes above the house. */}
              {(() => {
                const boxCenterX = Math.min(Math.max(rockX, 22 + 34), W - 22 - 34);
                const boxHeight = isNegativeIce ? 42 : 30;
                const boxY = Math.min(broomRenderY + 16, H - 16 - boxHeight);
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <rect x={boxCenterX - 34} y={boxY} width={68} height={boxHeight} rx={6}
                      fill="rgba(15,41,66,0.9)" stroke={isNegativeIce ? '#f87171' : 'rgba(255,255,255,0.2)'} strokeWidth={1} />
                    <text x={boxCenterX} y={boxY + 13} textAnchor="middle" fontSize={11} fontWeight="bold"
                      fill={zoneTimeEstimate.isEstimate ? '#fcd34d' : '#ffffff'}>
                      {zoneTimeEstimate.isEstimate ? '~' : ''}{zoneTimeEstimate.time.toFixed(1)}s
                    </text>
                    <text x={boxCenterX} y={boxY + 26} textAnchor="middle" fontSize={11} fontWeight="bold"
                      fill={isNegativeIce ? '#f87171' : (manualCurl === null && zoneCurlEstimate.isEstimate ? '#fcd34d' : '#ffffff')}>
                      {manualCurl === null && zoneCurlEstimate.isEstimate ? '~' : ''}{effectiveCurl.toFixed(1)} ft
                    </text>
                    {/* Called out explicitly, not just left to the color and the minus
                        sign — the rock has crossed the broom's own line entirely, which
                        reads as a plain magnitude difference otherwise. */}
                    {isNegativeIce && (
                      <text x={boxCenterX} y={boxY + 38} textAnchor="middle" fontSize={9} fontWeight="bold" fill="#f87171">
                        Negative ice
                      </text>
                    )}
                  </g>
                );
              })()}

              {/* Gray shadow of the broom on the tee line, vertically in line with the
                  actual broom head — skips place their own target broom on the tee
                  line regardless of the called zone, so this shows where that would
                  fall for wherever the broom is currently dragged. Omitted when the
                  broom is already on the tee line, since it would sit on top of it. */}
              {Math.abs(broomPos.y - teeLineY) > 2 && (
                <rect x={broomRenderX - 3} y={Y(teeLineY) - 12} width={6} height={24} rx={2}
                  fill="#94a3b8" fillOpacity={0.5} stroke="#64748b" strokeWidth={1} style={{ pointerEvents: 'none' }} />
              )}

              {/* Broom head — drag anywhere in the house; a larger invisible circle
                  around it makes it easier to grab than the 6px-wide handle alone. */}
              <g
                onPointerDown={handleBroomPointerDown}
                onPointerMove={handleBroomPointerMove}
                onPointerUp={handleBroomPointerUp}
                onPointerCancel={handleBroomPointerUp}
                style={{ cursor: 'grab', touchAction: 'none' }}
              >
                <circle cx={broomRenderX} cy={broomRenderY} r={16} fill="transparent" />
                <rect x={broomRenderX - 3} y={broomRenderY - 12} width={6} height={24} rx={2} fill="#facc15" stroke="#78350f" strokeWidth={1} />
              </g>

              {/* Turn toggle — sits just above the broom head and follows it around as
                  it's dragged; click to flip clockwise (red) / counter-clockwise (blue). */}
              <g
                onClick={() => setTurn(turn === 'clockwise' ? 'counter-clockwise' : 'clockwise')}
                style={{ cursor: 'pointer' }}
              >
                <rect
                  x={broomRenderX - 16}
                  y={Math.max(broomRenderY - 34, 24) - 8}
                  width={32}
                  height={16}
                  rx={8}
                  fill={turn === 'clockwise' ? '#ef4444' : '#3b82f6'}
                  fillOpacity={0.3}
                  stroke="#facc15"
                  strokeWidth={2}
                />
                <circle
                  cx={turn === 'clockwise' ? broomRenderX - 8 : broomRenderX + 8}
                  cy={Math.max(broomRenderY - 34, 24)}
                  r={7}
                  fill={turn === 'clockwise' ? '#ef4444' : '#3b82f6'}
                />
                <text
                  x={broomRenderX}
                  y={Math.max(broomRenderY - 34, 24) - 12}
                  textAnchor="middle"
                  fontSize={8}
                  fontWeight="bold"
                  fill="#ffffff"
                  style={{ pointerEvents: 'none' }}
                >
                  {turn === 'clockwise' ? 'CW' : 'CCW'}
                </text>
              </g>
            </svg>

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-slate-400">
              <span><span className="text-red-400 font-semibold">Red</span> = Clockwise / in-turn</span>
              <span><span className="text-blue-400 font-semibold">Blue</span> = Counter-clockwise / out-turn</span>
              <span className="w-full">
                <span className="text-amber-300 font-semibold italic">~</span> = estimated (from the mirrored
                path, practice shots, or a generic default) rather than a direct reading for this exact shot
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
              {HIT_ZONES.map(z => (
                <span key={z.zone}>{z.abbr} - {z.label}: {z.zone}</span>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Pick Draw or a hit type, then drag the broom anywhere in the house — click the switch above the broom
              head to flip the turn. For Draw, where you drop the broom sets both weight and curl; a hit type's
              weight is fixed. Estimated weight and curl update for that exact shot ({endType === 'away' ? 'Away' : 'Home'} end).
              Drag the rock itself to call a different amount of curl than the estimate, if this team isn't reading
              the same curl the ice has been showing.
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
